import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    FormGroup,
    Label,
    Input,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
    Button
} from 'reactstrap';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaDownload, FaPrint } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const baseURL = apiURL1; // Replace with your actual base URL

function StudentExamList() {
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState('');
    const [type, setType] = useState('');
    const [studentId, setStudentId] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [examTypes, setExamTypes] = useState([]);
    const [madrasaInfo, setMadrasaInfo] = useState({});
    const token = localStorage.getItem("token");


    useEffect(() => {
        // Retrieve user info from localStorage
        const user = localStorage.getItem('userInfo');
        if (user) {
            try {
                const parsedUser = JSON.parse(user);
                setStudentId(parsedUser._id);
            } catch (error) {
                console.error('Error parsing user info from localStorage:', error);
                toast.error('Error retrieving student ID.');
            }
        } else {
            toast.error('User info not found in localStorage.');
        }
    }, []);

    useEffect(() => {
        fetchExamTypes();
        fetchMadrasaInfo();
    }, []);

    const fetchMadrasaInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/student/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            }
            );
            const data = response.data;
            if (data && data.length > 0) {
                setMadrasaInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching madrasa info:', error);
        }
    };

    useEffect(() => {
        if (studentId) {
            fetchExams();
        }
    }, [studentId, year, type, page]);

    const fetchExamTypes = async () => {
        try {
            const response = await axios.get(`${baseURL}/student/getExamTypes`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                setExamTypes(response.data.data);
            } else {
                setExamTypes([]);
                if (response.data && !response.data.success) {
                    toast.error(response.data.message || 'Failed to fetch exam types');
                }
            }
        } catch (error) {
            console.error('Error fetching exam types:', error);
            toast.error('Error fetching exam types');
            setExamTypes([]);
        }
    };

    const fetchExams = async () => {
        setLoading(true);
        try {
            let url = `${baseURL}/student/${studentId}/exams?page=${page}`;
            if (year) url += `&year=${year}`;
            if (type) url += `&type=${type}`;
            // console.log('URL is ', url)

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            if (response.data && response.data.data) {
                setExams(response.data.data);
                setTotalPages(response.data.pagination.totalPages);
            } else {
                setExams([]);
                setTotalPages(0);
            }
        } catch (error) {
            console.error('Error fetching exams:', error);
            toast.error('Error fetching exams');
            setExams([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = (date) => {
        setYear(dayjs(date).format('YYYY'));
        setPage(1); // Reset page to 1 when year changes
    };

    const handleTypeChange = (e) => {
        setType(e.target.value);
        setPage(1); // Reset page to 1 when type changes
    };

    const renderPagination = () => {
        const paginationItems = [];
        for (let i = 1; i <= totalPages; i++) {
            paginationItems.push(
                <PaginationItem key={i} active={i === page}>
                    <PaginationLink onClick={() => setPage(i)}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination style={{ justifyContent: 'center' }}>
                <PaginationItem disabled={page === 1}>
                    <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {paginationItems}
                <PaginationItem disabled={page === totalPages}>
                    <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
            </Pagination>
        );
    };

    const generatePdfReport = () => {
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();

        // School Logo and Information (Header)
        const img = new Image();
        img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";

        pdf.addImage(img, 'PNG', 15, 15, 30, 30);

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(madrasaInfo.schoolName || 'Madrasa Name', 50, 22);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(madrasaInfo.schoolAddress || 'Madrasa Address', 50, 28);
        pdf.text(`Tel: ${madrasaInfo.schoolPhoneNumber || 'Phone'} | Email: ${madrasaInfo.schoolEmail || 'Email'}`, 50, 34);

        // Report Title
        pdf.setFontSize(18);
        pdf.text('Student Exam List', pageWidth / 2, 55, { align: 'center' });

        let y = 70;
        exams.forEach((exam) => {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`${exam.class?.className || '00'} - ${exam.course?.courseName || '00'} (${exam?.type?.name || '00'} - ${exam.year || '00'})`, 15, y);
            y += 10;

            const headers = [['Subject', 'Date', 'Start Time', 'End Time', 'Staff']];
            const data = exam.examDetails.map(detail => [
                detail.subject?.name || 'N/A',
                dayjs(detail.examDate).format('DD-MM-YYYY'),
                detail.startTime,
                detail.endTime,
                detail.teacher ? detail.teacher.name : 'N/A'
            ]);

            pdf.autoTable({
                startY: y,
                head: headers,
                body: data,
                margin: { left: 15 },
                columnStyles: { 0: { cellWidth: 40 } }
            });

            y = pdf.autoTable.previous.finalY + 15;
        });

        // Footer
        const totalPages = pdf.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);
            pdf.text(`Generated on: ${new Date().toLocaleDateString()} | ${madrasaInfo.schoolName || 'Madrasa Name'}`,
                pageWidth - 40, pdf.internal.pageSize.getHeight() - 10);
        }

        pdf.save('student_exam_list.pdf');
    };

    const handlePrint = () => {
        const tableStyle = `border-collapse: collapse; width: 100%; margin: 10px 0;`;
        const thStyle = `background-color: #2980b9; color: white; padding: 8px; text-align: left; border: 1px solid #ddd;`;
        const tdStyle = `padding: 8px; text-align: left; border: 1px solid #ddd;`;

        let printContent = `
            <html>
            <head>
                <title>Student Exam List</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .header { text-align: center; margin-bottom: 20px; }
                    .school-name { font-size: 24px; font-weight: bold; }
                    .school-info { font-size: 14px; }
                    .report-title { font-size: 20px; margin-bottom: 10px; }
                    table { ${tableStyle} }
                    th { ${thStyle} }
                    td { ${tdStyle} }
                    .footer { text-align: center; margin-top: 20px; font-size: 12px; }
                    .exam-card { margin-bottom: 20px; border: 1px solid #ddd; padding: 10px; }
                    .exam-title { font-size: 16px; font-weight: bold; }
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" alt="Madrasa Logo" width="100"/>
                    <div class="school-name">${madrasaInfo.schoolName || 'Madrasa Name'}</div>
                    <div class="school-info">
                        ${madrasaInfo.schoolAddress || 'Madrasa Address'}<br/>
                        Tel: ${madrasaInfo.schoolPhoneNumber || 'Phone'} | Email: ${madrasaInfo.schoolEmail || 'Email'}
                    </div>
                </div>
                <div class="report-title">Student Exam List</div>
        `;

        exams.forEach(exam => {
            printContent += `
                <div class="exam-card">
                    <div class="exam-title">${exam.class.className} - ${exam.course.courseName} (${exam?.type?.name} - ${exam.year})</div>
                    <table border="1">
                        <thead>
                            <tr>
                                <th>Subject</th>
                                <th>Date</th>
                                <th>Start Time</th>
                                <th>End Time</th>
                                <th>Staff</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${exam.examDetails.map(detail => `
                                <tr>
                                    <td style="${tdStyle}">${detail.subject?.name || 'N/A'}</td>
                                    <td style="${tdStyle}">${dayjs(detail.examDate).format('DD-MM-YYYY')}</td>
                                    <td style="${tdStyle}">${detail.startTime}</td>
                                    <td style="${tdStyle}">${detail.endTime}</td>
                                    <td style="${tdStyle}">${detail.teacher ? detail.teacher.name : 'N/A'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            `;
        });

        printContent += `
                <div class="footer">Generated on: ${new Date().toLocaleDateString()}</div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '', 'width=800,height=600');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <>
            <Header />
            <Container>
                <Row className="mb-3">
                    <Col md={6}>
                        <FormGroup>
                            <Label>Year</Label>
                            <DatePicker
                                selected={year ? dayjs(year, 'YYYY').toDate() : null}
                                onChange={handleYearChange}
                                showYearPicker
                                dateFormat="yyyy"
                                yearItemNumber={9}
                                className="form-control"
                                placeholderText="Select Year"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Type</Label>
                            <Input type="select" value={type} onChange={handleTypeChange}>
                                <option value="">All Types</option>
                                {examTypes.map((examType) => (
                                    <option key={examType._id} value={examType._id}>
                                        {examType.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div className="d-flex justify-content-end mb-3">
                            <Button color="primary" onClick={generatePdfReport} className="mr-2">
                                <FaDownload /> Download PDF
                            </Button>
                            <Button color="info" onClick={handlePrint}>
                                <FaPrint /> Print
                            </Button>
                        </div>
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Spinner color="primary" />
                            </div>
                        ) : (
                            <>
                                {exams.length === 0 ? (
                                    <p>No exams found.</p>
                                ) : (
                                    exams.map((exam) => (
                                        <Card key={exam._id} className="mb-3">
                                            <CardHeader>
                                                <h5>{exam.class.className} - {exam.course.courseName} ({exam?.type?.name} - {exam.year})</h5>
                                            </CardHeader>
                                            <CardBody>
                                                <Table>
                                                    <thead>
                                                        <tr>
                                                            <th>Subject</th>
                                                            <th>Date</th>
                                                            <th>Start Time</th>
                                                            <th>End Time</th>
                                                            <th>Staff</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {exam.examDetails.map((detail) => (
                                                            <tr key={detail._id}>
                                                                <td>{detail.subject?.name || 'N/A'}</td>
                                                                <td>{dayjs(detail.examDate).format('DD-MM-YYYY')}</td>
                                                                <td>{detail.startTime}</td>
                                                                <td>{detail.endTime}</td>
                                                                <td>{detail.teacher ? detail.teacher.name : 'N/A'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </CardBody>
                                        </Card>
                                    ))
                                )}
                            </>
                        )}
                        {renderPagination()}
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default StudentExamList;
